import React, { useState, useMemo, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Building2,
  User,
  Plus,
  Edit,
  Trash2,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Mail,
  Phone,
  Calendar,
  MessageSquare,
  Smile,
  AlertCircle,
  UserPlus,
  Power,
  History,
  FileText,
  Users,
  Shield,
  ChevronsUpDown,
  GitBranch,
  FileDown,
  Loader2
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import type { Client, ContactPerson } from "@shared/schema";
import { ClientResources } from "./client-resources";
import { useAuth } from "@/contexts/auth-context";

import { SensitivityBadge } from "@/lib/sensitivity.tsx";

const CONTACT_CATEGORIES = ['Freelancer', 'Partner', 'Client Division', 'Supplier', 'Other'] as const;

// Custom hook for fetching clients
function useClients() {
  return useQuery({
    queryKey: ["/api/clients"],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/clients');
      if (!response.ok) throw new Error("Failed to fetch clients");
      return response.json() as Promise<Client[]>;
    },
  });
}

// Task #66: global standard email templates now live server-side
function useStandardEmailTemplates() {
  return useQuery({
    queryKey: ["/api/settings/email-standard-templates"],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/settings/email-standard-templates');
      if (!response.ok) throw new Error("Failed to fetch standard email templates");
      return response.json() as Promise<{ preTemplate: string; postTemplate: string }>;
    },
    staleTime: 60_000,
  });
}

// Custom hook for fetching contact persons
function useContactPersons(clientId?: string) {
  return useQuery({
    queryKey: clientId ? ["/api/contact-persons", clientId] : ["/api/contact-persons"],
    queryFn: async () => {
      const url = clientId 
        ? `/api/contact-persons?clientId=${clientId}` 
        : "/api/contact-persons";
      const response = await apiRequest('GET', url);
      if (!response.ok) throw new Error("Failed to fetch contact persons");
      return response.json() as Promise<ContactPerson[]>;
    },
    enabled: true
  });
}

export function ClientSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { data: clients = [], isLoading: clientsLoading } = useClients();
  const { data: allContactPersons = [], isLoading: personsLoading } = useContactPersons();
  
  const [expandedClients, setExpandedClients] = useState<Record<string, boolean>>({});
  const [selectedTab, setSelectedTab] = useState("clients");
  const [showNewClientDialog, setShowNewClientDialog] = useState(false);
  const [showNewContactDialog, setShowNewContactDialog] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [reportClient, setReportClient] = useState<Client | null>(null);
  const [editingContact, setEditingContact] = useState<ContactPerson | null>(null);
  const { data: standardTemplates } = useStandardEmailTemplates();
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ type: 'client' | 'contact', id: string } | null>(null);
  const [selectedClientForContact, setSelectedClientForContact] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Form states
  const [newClient, setNewClient] = useState({
    id: "",
    companyName: "",
    clientEmailMain: "",
    clientEmailSecondaries: [""],
    phone: "",
    notes: "",
    parentClientId: null as string | null
  });
  const [parentComboOpen, setParentComboOpen] = useState(false);
  const [editParentComboOpen, setEditParentComboOpen] = useState(false);
  
  const [newContact, setNewContact] = useState({
    clientId: "",
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    whatsappNumber: "",
    landlineNumber: "",
    position: "",
    status: "Active",
    preferredCommunication: "email",
    happinessBaseline: 50,
    preEmailTemplate: "",
    postEmailTemplate: "",
    notes: ""
  });

  // Additional email addresses of the contact being edited (admin only)
  const [newAliasEmail, setNewAliasEmail] = useState("");

  // Mutations
  const { data: contactEmails } = useQuery<{ primary: string; aliases: Array<{ email: string; dateAdded: string }> }>({
    queryKey: ['/api/contact-persons', editingContact?.id, 'emails'],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/contact-persons/${editingContact!.id}/emails`);
      if (!res.ok) throw new Error('Failed to load email addresses');
      return res.json();
    },
    enabled: !!editingContact && isAdmin,
  });

  // Capture the contact id in the mutation variables so a contact switch
  // mid-request can never invalidate (or hit) the wrong contact's alias list.
  const addAliasMutation = useMutation({
    mutationFn: async ({ contactId, email }: { contactId: string; email: string }) => {
      const res = await apiRequest('POST', `/api/contact-persons/${contactId}/emails`, { email });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to add email address');
      }
      return res.json();
    },
    onSuccess: (_data, { contactId }) => {
      setNewAliasEmail("");
      queryClient.invalidateQueries({ queryKey: ['/api/contact-persons', contactId, 'emails'] });
      toast({ title: "Email address added" });
    },
    onError: (error: Error) => {
      toast({ title: "Could not add email address", description: error.message, variant: "destructive" });
    }
  });

  const removeAliasMutation = useMutation({
    mutationFn: async ({ contactId, email }: { contactId: string; email: string }) => {
      const res = await apiRequest('DELETE', `/api/contact-persons/${contactId}/emails?email=${encodeURIComponent(email)}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to remove email address');
      }
      return res.json();
    },
    onSuccess: (_data, { contactId }) => {
      queryClient.invalidateQueries({ queryKey: ['/api/contact-persons', contactId, 'emails'] });
      toast({ title: "Email address removed" });
    },
    onError: (error: Error) => {
      toast({ title: "Could not remove email address", description: error.message, variant: "destructive" });
    }
  });

  // Reset the alias input whenever a different contact is opened (or the
  // dialog closes) so text can't carry over between contacts.
  useEffect(() => {
    setNewAliasEmail("");
  }, [editingContact?.id]);

  const createClientMutation = useMutation({
    mutationFn: async (clientData: any) => {
      const response = await apiRequest('POST', '/api/clients', {
        ...clientData,
        isActive: true
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to create client");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      setShowNewClientDialog(false);
      resetNewClientForm();
      toast({ title: "Client created successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Failed to create client", 
        description: error.message,
        variant: "destructive" 
      });
    }
  });

  const updateClientMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await apiRequest('PATCH', `/api/clients/${id}`, data);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to update client");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      setEditingClient(null);
      toast({ title: "Client updated successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Failed to update client", 
        description: error.message,
        variant: "destructive" 
      });
    }
  });

  const createContactMutation = useMutation({
    mutationFn: async (contactData: any) => {
      const response = await apiRequest('POST', '/api/contact-persons', contactData);
      if (!response.ok) throw new Error("Failed to create contact person");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contact-persons"] });
      setShowNewContactDialog(false);
      resetNewContactForm();
      toast({ title: "Contact person created successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Failed to create contact person", 
        description: error.message,
        variant: "destructive" 
      });
    }
  });

  const updateContactMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await apiRequest('PATCH', `/api/contact-persons/${id}`, data);
      if (!response.ok) throw new Error("Failed to update contact person");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contact-persons"] });
      setEditingContact(null);
      toast({ title: "Contact person updated successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Failed to update contact person", 
        description: error.message,
        variant: "destructive" 
      });
    }
  });

  const deleteContactMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest('DELETE', `/api/contact-persons/${id}`);
      if (!response.ok) throw new Error("Failed to delete contact person");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contact-persons"] });
      toast({ title: "Contact person deleted successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Failed to delete contact person", 
        description: error.message,
        variant: "destructive" 
      });
    }
  });

  const updateInteractionMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest('POST', `/api/contact-persons/${id}/interaction`);
      if (!response.ok) throw new Error("Failed to update interaction");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contact-persons"] });
      toast({ title: "Interaction recorded" });
    }
  });

  // Helper functions
  const resetNewClientForm = () => {
    setNewClient({
      id: "",
      companyName: "",
      clientEmailMain: "",
      clientEmailSecondaries: [""],
      phone: "",
      notes: "",
      parentClientId: null
    });
  };

  const resetNewContactForm = () => {
    setNewContact({
      clientId: "",
      firstName: "",
      lastName: "",
      email: "",
      mobileNumber: "",
      whatsappNumber: "",
      landlineNumber: "",
      position: "",
      status: "Active",
      preferredCommunication: "email",
      happinessBaseline: 50,
      preEmailTemplate: "",
      postEmailTemplate: "",
      notes: ""
    });
  };

  const validateClientData = (data: typeof newClient) => {
    const errors: string[] = [];
    
    if (!data.id || data.id.length !== 2) {
      errors.push("Client code must be exactly 2 letters");
    }
    if (!/^[A-Z]{2}$/.test(data.id)) {
      errors.push("Client code must be 2 uppercase letters");
    }
    if (!data.companyName) {
      errors.push("Company name is required");
    }
    if (!data.clientEmailMain) {
      errors.push("Main email is required");
    }
    
    return { isValid: errors.length === 0, errors };
  };

  const handleCreateClient = () => {
    const validation = validateClientData(newClient);
    if (!validation.isValid) {
      toast({ 
        title: "Validation Error", 
        description: validation.errors.join(', '), 
        variant: "destructive" 
      });
      return;
    }
    
    const filteredSecondaries = newClient.clientEmailSecondaries.filter(email => email.trim() !== "");
    createClientMutation.mutate({
      ...newClient,
      clientEmailSecondaries: filteredSecondaries
    });
  };

  const handleCreateContact = () => {
    if (!newContact.clientId) {
      toast({ 
        title: "Validation Error", 
        description: "Please select a client", 
        variant: "destructive" 
      });
      return;
    }
    if (!newContact.firstName || !newContact.lastName || !newContact.email) {
      toast({ 
        title: "Validation Error", 
        description: "First name, last name, and email are required", 
        variant: "destructive" 
      });
      return;
    }
    
    createContactMutation.mutate(newContact);
  };

  const toggleClientExpansion = (clientId: string) => {
    setExpandedClients(prev => ({
      ...prev,
      [clientId]: !prev[clientId]
    }));
  };

  const getContactsForClient = (clientId: string) => {
    return allContactPersons.filter(person => person.clientId === clientId);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'bg-green-600';
      case 'inactive': return 'bg-gray-600';
      case 'on leave': return 'bg-yellow-600';
      default: return 'bg-gray-600';
    }
  };

  const getTemplateStatus = (contact: ContactPerson) => {
    const preTemplateOk = contact.preEmailTemplate && contact.preEmailTemplate.length > 10;
    const postTemplateOk = contact.postEmailTemplate && contact.postEmailTemplate.length > 10;
    
    return {
      isComplete: preTemplateOk && postTemplateOk,
      text: preTemplateOk && postTemplateOk ? 'Template OK' : 'Template Missing',
      className: preTemplateOk && postTemplateOk ? 'bg-green-600' : 'bg-orange-600'
    };
  };

  const hasTemplateMissing = (clientId: string) => {
    const contacts = getContactsForClient(clientId);
    return contacts.some(contact => {
      const preTemplateOk = contact.preEmailTemplate && contact.preEmailTemplate.length > 10;
      const postTemplateOk = contact.postEmailTemplate && contact.postEmailTemplate.length > 10;
      return !(preTemplateOk && postTemplateOk);
    });
  };

  const getDescendantIds = (clientId: string, allClients: Client[]): Set<string> => {
    const descendants = new Set<string>();
    const queue = [clientId];
    while (queue.length > 0) {
      const current = queue.shift()!;
      const children = allClients.filter(c => c.parentClientId === current && c.id !== clientId);
      for (const child of children) {
        if (!descendants.has(child.id)) {
          descendants.add(child.id);
          queue.push(child.id);
        }
      }
    }
    return descendants;
  };

  const hierarchicalClients = useMemo(() => {
    if (!isAdmin) return clients;
    const topLevel = clients.filter(c => !c.parentClientId);
    const result: (Client & { _isChild?: boolean })[] = [];
    for (const parent of topLevel) {
      result.push(parent);
      const children = clients.filter(c => c.parentClientId === parent.id);
      for (const child of children) {
        result.push({ ...child, _isChild: true });
      }
    }
    const placed = new Set(result.map(c => c.id));
    for (const c of clients) {
      if (!placed.has(c.id)) {
        result.push({ ...c, _isChild: !!c.parentClientId });
      }
    }
    return result;
  }, [clients, isAdmin]);

  return (
    <div className="space-y-6">
      <div className="text-sm text-gray-300 mb-4">
        <p>Manage clients and their contact persons. Track communication preferences, happiness levels, and project assignments.</p>
      </div>

      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="bg-gray-800 border-gray-700">
          <TabsTrigger value="clients" className="data-[state=active]:bg-gray-700">
            <Building2 className="w-4 h-4 mr-2" />
            Clients
          </TabsTrigger>
          <TabsTrigger value="contacts" className="data-[state=active]:bg-gray-700">
            <Users className="w-4 h-4 mr-2" />
            All Contacts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-white">Client Management</h3>
            <Button
              onClick={() => setShowNewClientDialog(true)}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </div>

          {clientsLoading ? (
            <div className="text-gray-400">Loading clients...</div>
          ) : (
            <div className="space-y-3">
              {hierarchicalClients.map((client) => {
                const isChild = isAdmin && '_isChild' in client && client._isChild;
                const parentName = isAdmin && client.parentClientId
                  ? clients.find(c => c.id === client.parentClientId)?.companyName
                  : undefined;
                return (
                <Card key={client.id} className={`navy-card border-gray-600 ${isChild ? 'ml-8 border-l-2 border-l-blue-500/30' : ''}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleClientExpansion(client.id)}
                          className="p-1 h-auto"
                        >
                          {expandedClients[client.id] ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}
                        </Button>
                        <Badge variant="outline" className="font-mono">
                          {client.id}
                        </Badge>
                        <span className="text-white font-medium">{client.companyName}</span>
                        {isAdmin && parentName && (
                          <span className="flex items-center gap-1 text-xs text-blue-400">
                            <GitBranch className="w-3 h-3" />
                            subsidiary of {parentName}
                          </span>
                        )}
                        {!client.isActive && (
                          <Badge className="bg-gray-600">Inactive</Badge>
                        )}
                        {hasTemplateMissing(client.id) && (
                          <Badge className="bg-orange-600">Template Missing</Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setReportClient(client)}
                            className="text-gray-400 hover:text-white"
                            title="Generate PDF report for this client"
                            data-testid={`button-report-client-${client.id}`}
                          >
                            <FileDown className="w-4 h-4 mr-1" />
                            <span className="text-xs">Report</span>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingClient(client)}
                          className="text-gray-400 hover:text-white"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-400 mt-2 flex gap-4">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {client.clientEmailMain}
                      </span>
                      {client.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {client.phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {getContactsForClient(client.id).length} contacts
                      </span>
                    </div>
                  </CardHeader>
                  
                  {expandedClients[client.id] && (
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="text-sm font-medium text-gray-300">Contact Persons</h4>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedClientForContact(client.id);
                              setNewContact(prev => ({ ...prev, clientId: client.id }));
                              setShowNewContactDialog(true);
                            }}
                            className="h-7 text-xs"
                          >
                            <UserPlus className="w-3 h-3 mr-1" />
                            Add Contact
                          </Button>
                        </div>
                        
                        {getContactsForClient(client.id).length === 0 ? (
                          <div className="text-sm text-gray-500 py-3 text-center border border-dashed border-gray-600 rounded">
                            No contact persons added yet
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {getContactsForClient(client.id).map((contact) => (
                              <div
                                key={contact.id}
                                className="p-3 bg-gray-800/50 rounded-lg border border-gray-700"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="space-y-1 flex-1">
                                    <div className="flex items-center gap-2">
                                      <SensitivityBadge level={contact.sensitivityLevel ?? 0} />
                                      <span className="font-medium text-white">{contact.firstName} {contact.lastName}</span>
                                      <Badge className={getStatusColor(contact.status)} variant="secondary">
                                        {contact.status}
                                      </Badge>
                                      <Badge className={getTemplateStatus(contact).className} variant="secondary">
                                        {getTemplateStatus(contact).text}
                                      </Badge>
                                      {isAdmin && contact.category && (
                                        <Badge variant="outline" className="text-[10px] border-gray-500 text-gray-400">
                                          {contact.category}
                                        </Badge>
                                      )}
                                      {contact.position && (
                                        <span className="text-xs text-gray-400">• {contact.position}</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-4 text-xs text-gray-400">
                                      <span className="flex items-center gap-1">
                                        <Mail className="w-3 h-3" />
                                        {contact.email}
                                      </span>
                                      {contact.mobileNumber && (
                                        <span className="flex items-center gap-1">
                                          <Phone className="w-3 h-3" />
                                          {contact.mobileNumber}
                                        </span>
                                      )}
                                      <span className="flex items-center gap-1">
                                        <MessageSquare className="w-3 h-3" />
                                        {contact.preferredCommunication}
                                      </span>
                                      <span className="flex items-center gap-1 text-gray-400">
                                        <Smile className="w-3 h-3" />
                                        {contact.happinessBaseline}%
                                      </span>
                                    </div>
                                    {contact.lastInteractionDate && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        Last interaction: {new Date(contact.lastInteractionDate).toLocaleDateString()}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => updateInteractionMutation.mutate(contact.id)}
                                      className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                                      title="Mark interaction"
                                    >
                                      <Calendar className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setEditingContact(contact)}
                                      className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                                    >
                                      <Edit className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setDeleteConfirmation({ type: 'contact', id: contact.id })}
                                      className="h-7 w-7 p-0 text-gray-400 hover:text-red-400"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="border-t border-gray-700 pt-4 mt-4">
                          <ClientResources clientId={client.id} />
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="contacts" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-white">All Contact Persons</h3>
            <Button
              onClick={() => setShowNewContactDialog(true)}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Contact
            </Button>
          </div>

          {/* Search input */}
          <div className="relative">
            <Input
              type="text"
              placeholder="Search by first name, last name, or company name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 navy-bg border-gray-600 text-white placeholder-gray-400"
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>

          {personsLoading ? (
            <div className="text-gray-400">Loading contacts...</div>
          ) : (
            <Card className="navy-card border-gray-600">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-600">
                      <TableHead className="text-gray-300">Name</TableHead>
                      <TableHead className="text-gray-300">Company</TableHead>
                      <TableHead className="text-gray-300">Email</TableHead>
                      <TableHead className="text-gray-300">Phone</TableHead>
                      <TableHead className="text-gray-300">Status</TableHead>
                      <TableHead className="text-gray-300">Happiness</TableHead>
                      <TableHead className="text-gray-300">Last Contact</TableHead>
                      <TableHead className="text-gray-300 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allContactPersons
                      .filter((contact) => {
                        if (!searchQuery.trim()) return true;
                        
                        const client = clients.find(c => c.id === contact.clientId);
                        const searchTerms = searchQuery.toLowerCase().split(/\s+/);
                        
                        // Combine all searchable fields
                        const searchableText = [
                          contact.firstName?.toLowerCase() || '',
                          contact.lastName?.toLowerCase() || '',
                          client?.companyName?.toLowerCase() || ''
                        ].join(' ');
                        
                        // Check if all search terms are found in the searchable text
                        return searchTerms.every(term => searchableText.includes(term));
                      })
                      .map((contact) => {
                      const client = clients.find(c => c.id === contact.clientId);
                      return (
                        <TableRow key={contact.id} className="border-gray-600">
                          <TableCell className="text-white font-medium">
                            <div className="flex items-center gap-2">
                              <SensitivityBadge level={contact.sensitivityLevel ?? 0} />
                              <div>
                                {contact.firstName} {contact.lastName}
                                {contact.position && (
                                  <span className="text-xs text-gray-400 block">{contact.position}</span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono mr-2">
                              {contact.clientId}
                            </Badge>
                            <span className="text-gray-300">{client?.companyName}</span>
                          </TableCell>
                          <TableCell className="text-gray-300">{contact.email}</TableCell>
                          <TableCell className="text-gray-300">{contact.mobileNumber || '-'}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Badge className={getStatusColor(contact.status)} variant="secondary">
                                {contact.status}
                              </Badge>
                              <Badge className={getTemplateStatus(contact).className} variant="secondary">
                                {getTemplateStatus(contact).text}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-gray-300">
                              {contact.happinessBaseline}%
                            </span>
                          </TableCell>
                          <TableCell className="text-gray-300">
                            {contact.lastInteractionDate 
                              ? new Date(contact.lastInteractionDate).toLocaleDateString()
                              : '-'
                            }
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => updateInteractionMutation.mutate(contact.id)}
                                className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                                title="Mark interaction"
                              >
                                <Calendar className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingContact(contact)}
                                className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteConfirmation({ type: 'contact', id: contact.id })}
                                className="h-7 w-7 p-0 text-gray-400 hover:text-red-400"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* New Client Dialog */}
      <Dialog open={showNewClientDialog} onOpenChange={setShowNewClientDialog}>
        <DialogContent className="bg-gray-800 text-white border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="client-code">Client Code</Label>
                <Input
                  id="client-code"
                  value={newClient.id}
                  onChange={(e) => setNewClient(prev => ({ ...prev, id: e.target.value.toUpperCase() }))}
                  placeholder="WE"
                  maxLength={2}
                  className="navy-bg border-gray-600 text-white font-mono"
                />
              </div>
              <div>
                <Label htmlFor="company-name">Company Name</Label>
                <Input
                  id="company-name"
                  value={newClient.companyName}
                  onChange={(e) => setNewClient(prev => ({ ...prev, companyName: e.target.value }))}
                  placeholder="Company Name"
                  className="navy-bg border-gray-600 text-white"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="main-email">Main Email</Label>
              <Input
                id="main-email"
                type="email"
                value={newClient.clientEmailMain}
                onChange={(e) => setNewClient(prev => ({ ...prev, clientEmailMain: e.target.value }))}
                placeholder="contact@company.com"
                className="navy-bg border-gray-600 text-white"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={newClient.phone}
                onChange={(e) => setNewClient(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1234567890"
                className="navy-bg border-gray-600 text-white"
              />
            </div>
            {isAdmin && (
              <div>
                <Label>Parent Client</Label>
                <Popover open={parentComboOpen} onOpenChange={setParentComboOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={parentComboOpen}
                      className="w-full justify-between navy-bg border-gray-600 text-white hover:bg-gray-700"
                    >
                      {newClient.parentClientId
                        ? clients.find(c => c.id === newClient.parentClientId)?.companyName || 'Select parent...'
                        : 'None (top-level client)'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0 bg-gray-800 border-gray-600">
                    <Command className="bg-gray-800">
                      <CommandInput placeholder="Search clients..." className="text-white" />
                      <CommandList>
                        <CommandEmpty className="text-gray-400 p-2 text-sm">No clients found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="__none__"
                            onSelect={() => {
                              setNewClient(prev => ({ ...prev, parentClientId: null }));
                              setParentComboOpen(false);
                            }}
                            className="text-gray-300"
                          >
                            <Check className={`mr-2 h-4 w-4 ${!newClient.parentClientId ? 'opacity-100' : 'opacity-0'}`} />
                            None (top-level client)
                          </CommandItem>
                          {clients
                            .filter(c => c.id !== newClient.id)
                            .map(c => (
                              <CommandItem
                                key={c.id}
                                value={`${c.companyName} ${c.id}`}
                                onSelect={() => {
                                  setNewClient(prev => ({ ...prev, parentClientId: c.id }));
                                  setParentComboOpen(false);
                                }}
                                className="text-gray-300"
                              >
                                <Check className={`mr-2 h-4 w-4 ${newClient.parentClientId === c.id ? 'opacity-100' : 'opacity-0'}`} />
                                <span className="font-mono mr-2 text-gray-500">{c.id}</span>
                                {c.companyName}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={newClient.notes}
                onChange={(e) => setNewClient(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                className="navy-bg border-gray-600 text-white"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewClientDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateClient} className="bg-blue-600 hover:bg-blue-700">
              Create Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Contact Dialog */}
      <Dialog open={showNewContactDialog} onOpenChange={setShowNewContactDialog}>
        <DialogContent className="bg-gray-800 text-white border-gray-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Contact Person</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact-firstname">First Name</Label>
                <Input
                  id="contact-firstname"
                  value={newContact.firstName}
                  onChange={(e) => setNewContact(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="First Name"
                  className="navy-bg border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="contact-lastname">Last Name</Label>
                <Input
                  id="contact-lastname"
                  value={newContact.lastName}
                  onChange={(e) => setNewContact(prev => ({ ...prev, lastName: e.target.value }))}
                  placeholder="Last Name"
                  className="navy-bg border-gray-600 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact-client">Client</Label>
                <Select
                  value={newContact.clientId}
                  onValueChange={(value) => setNewContact(prev => ({ ...prev, clientId: value }))}
                >
                  <SelectTrigger className="navy-bg border-gray-600 text-white">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        [{client.id}] {client.companyName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="contact-position">Position</Label>
                <Input
                  id="contact-position"
                  value={newContact.position}
                  onChange={(e) => setNewContact(prev => ({ ...prev, position: e.target.value }))}
                  placeholder="Job Title"
                  className="navy-bg border-gray-600 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact-email">Email</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@company.com"
                  className="navy-bg border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="contact-phone">Mobile Number</Label>
                <Input
                  id="contact-phone"
                  value={newContact.mobileNumber}
                  onChange={(e) => setNewContact(prev => ({ ...prev, mobileNumber: e.target.value }))}
                  placeholder="+1234567890"
                  className="navy-bg border-gray-600 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact-whatsapp">WhatsApp Number</Label>
                <Input
                  id="contact-whatsapp"
                  value={newContact.whatsappNumber}
                  onChange={(e) => setNewContact(prev => ({ ...prev, whatsappNumber: e.target.value }))}
                  placeholder="+1234567890"
                  className="navy-bg border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="contact-landline">Landline Number</Label>
                <Input
                  id="contact-landline"
                  value={newContact.landlineNumber}
                  onChange={(e) => setNewContact(prev => ({ ...prev, landlineNumber: e.target.value }))}
                  placeholder="+49 30 1234567"
                  className="navy-bg border-gray-600 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact-status">Status</Label>
                <Select
                  value={newContact.status}
                  onValueChange={(value) => setNewContact(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger className="navy-bg border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                    <SelectItem value="On Leave">On Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="contact-communication">Preferred Communication</Label>
                <Select
                  value={newContact.preferredCommunication}
                  onValueChange={(value) => setNewContact(prev => ({ ...prev, preferredCommunication: value }))}
                >
                  <SelectTrigger className="navy-bg border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="slack">Slack</SelectItem>
                    <SelectItem value="teams">Teams</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="happiness">Happiness Baseline (%)</Label>
              <Input
                id="happiness"
                type="number"
                value={newContact.happinessBaseline}
                onChange={(e) => setNewContact(prev => ({ ...prev, happinessBaseline: parseInt(e.target.value) || 50 }))}
                min={0}
                max={100}
                placeholder="50"
                className="navy-bg border-gray-600 text-white"
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label htmlFor="pre-email">Pre-Email Template</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const standardTemplate = standardTemplates?.preTemplate || '';
                    const personalizedTemplate = standardTemplate.replace(/{firstName}/g, newContact.firstName || 'Customer');
                    setNewContact(prev => ({ ...prev, preEmailTemplate: personalizedTemplate }));
                  }}
                  className="text-xs h-7"
                >
                  Use Standard Template
                </Button>
              </div>
              <Textarea
                id="pre-email"
                value={newContact.preEmailTemplate}
                onChange={(e) => setNewContact(prev => ({ ...prev, preEmailTemplate: e.target.value }))}
                placeholder="Template for email greeting/opening..."
                className="navy-bg border-gray-600 text-white"
                rows={3}
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label htmlFor="post-email">Post-Email Template</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const standardTemplate = standardTemplates?.postTemplate || '';
                    setNewContact(prev => ({ ...prev, postEmailTemplate: standardTemplate }));
                  }}
                  className="text-xs h-7"
                >
                  Use Standard Template
                </Button>
              </div>
              <Textarea
                id="post-email"
                value={newContact.postEmailTemplate}
                onChange={(e) => setNewContact(prev => ({ ...prev, postEmailTemplate: e.target.value }))}
                placeholder="Template for email closing/signature..."
                className="navy-bg border-gray-600 text-white"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="contact-notes">Notes</Label>
              <Textarea
                id="contact-notes"
                value={newContact.notes}
                onChange={(e) => setNewContact(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes about this contact..."
                className="navy-bg border-gray-600 text-white"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewContactDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateContact} className="bg-blue-600 hover:bg-blue-700">
              Create Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      {editingClient && (
        <Dialog open={!!editingClient} onOpenChange={() => setEditingClient(null)}>
          <DialogContent className="bg-gray-800 text-white border-gray-700 max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Client: {editingClient.companyName}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-company-name">Company Name</Label>
                <Input
                  id="edit-company-name"
                  value={editingClient.companyName}
                  onChange={(e) => setEditingClient(prev => prev ? { ...prev, companyName: e.target.value } : null)}
                  className="navy-bg border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="edit-main-email">Main Email</Label>
                <Input
                  id="edit-main-email"
                  type="email"
                  value={editingClient.clientEmailMain}
                  onChange={(e) => setEditingClient(prev => prev ? { ...prev, clientEmailMain: e.target.value } : null)}
                  className="navy-bg border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={editingClient.phone || ''}
                  onChange={(e) => setEditingClient(prev => prev ? { ...prev, phone: e.target.value } : null)}
                  className="navy-bg border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="edit-logo">Logo (PNG, for white-label shares)</Label>
                <div className="flex items-center gap-3 mt-1">
                  {editingClient.logoPng ? (
                    <img
                      src={editingClient.logoPng}
                      alt="Client logo"
                      className="h-10 max-w-[120px] object-contain rounded bg-gray-900/60 border border-gray-600 p-1"
                    />
                  ) : (
                    <span className="text-xs text-gray-500">No logo</span>
                  )}
                  <input
                    id="edit-logo"
                    type="file"
                    accept="image/png"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file) return;
                      if (file.type !== 'image/png') {
                        toast({ title: 'Logo must be a PNG (with transparency)', variant: 'destructive' });
                        return;
                      }
                      if (file.size > 2 * 1024 * 1024) {
                        toast({ title: 'Logo too large (max 2 MB)', variant: 'destructive' });
                        return;
                      }
                      const reader = new FileReader();
                      reader.onload = () => {
                        const dataUrl = typeof reader.result === 'string' ? reader.result : null;
                        if (dataUrl) setEditingClient(prev => prev ? { ...prev, logoPng: dataUrl } : null);
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-gray-600 text-gray-300"
                    onClick={() => document.getElementById('edit-logo')?.click()}
                  >
                    {editingClient.logoPng ? 'Replace…' : 'Upload PNG…'}
                  </Button>
                  {editingClient.logoPng && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-red-400"
                      onClick={() => setEditingClient(prev => prev ? { ...prev, logoPng: null } : null)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="edit-status">Status</Label>
                <Select
                  value={editingClient.isActive ? 'active' : 'inactive'}
                  onValueChange={(value) => setEditingClient(prev => prev ? { ...prev, isActive: value === 'active' } : null)}
                >
                  <SelectTrigger className="navy-bg border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isAdmin && (() => {
                const excludedIds = getDescendantIds(editingClient.id, clients);
                excludedIds.add(editingClient.id);
                return (
                  <div>
                    <Label>Parent Client</Label>
                    <Popover open={editParentComboOpen} onOpenChange={setEditParentComboOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={editParentComboOpen}
                          className="w-full justify-between navy-bg border-gray-600 text-white hover:bg-gray-700"
                        >
                          {editingClient.parentClientId
                            ? clients.find(c => c.id === editingClient.parentClientId)?.companyName || 'Select parent...'
                            : 'None (top-level client)'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0 bg-gray-800 border-gray-600">
                        <Command className="bg-gray-800">
                          <CommandInput placeholder="Search clients..." className="text-white" />
                          <CommandList>
                            <CommandEmpty className="text-gray-400 p-2 text-sm">No clients found.</CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                value="__none__"
                                onSelect={() => {
                                  setEditingClient(prev => prev ? { ...prev, parentClientId: null } : null);
                                  setEditParentComboOpen(false);
                                }}
                                className="text-gray-300"
                              >
                                <Check className={`mr-2 h-4 w-4 ${!editingClient.parentClientId ? 'opacity-100' : 'opacity-0'}`} />
                                None (top-level client)
                              </CommandItem>
                              {clients
                                .filter(c => !excludedIds.has(c.id))
                                .map(c => (
                                  <CommandItem
                                    key={c.id}
                                    value={`${c.companyName} ${c.id}`}
                                    onSelect={() => {
                                      setEditingClient(prev => prev ? { ...prev, parentClientId: c.id } : null);
                                      setEditParentComboOpen(false);
                                    }}
                                    className="text-gray-300"
                                  >
                                    <Check className={`mr-2 h-4 w-4 ${editingClient.parentClientId === c.id ? 'opacity-100' : 'opacity-0'}`} />
                                    <span className="font-mono mr-2 text-gray-500">{c.id}</span>
                                    {c.companyName}
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                );
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingClient(null)}>
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  if (editingClient) {
                    updateClientMutation.mutate({
                      id: editingClient.id,
                      data: {
                        companyName: editingClient.companyName,
                        clientEmailMain: editingClient.clientEmailMain,
                        phone: editingClient.phone,
                        isActive: editingClient.isActive,
                        parentClientId: editingClient.parentClientId ?? null,
                        logoPng: editingClient.logoPng ?? null
                      }
                    });
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Update Client
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Contact Dialog */}
      {editingContact && (
        <Dialog open={!!editingContact} onOpenChange={() => setEditingContact(null)}>
          <DialogContent className="bg-gray-800 text-white border-gray-700 max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Contact: {editingContact.firstName} {editingContact.lastName}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={editingContact.firstName}
                    onChange={(e) => setEditingContact(prev => prev ? { ...prev, firstName: e.target.value } : null)}
                    className="navy-bg border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={editingContact.lastName}
                    onChange={(e) => setEditingContact(prev => prev ? { ...prev, lastName: e.target.value } : null)}
                    className="navy-bg border-gray-600 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Position</Label>
                  <Input
                    value={editingContact.position || ''}
                    onChange={(e) => setEditingContact(prev => prev ? { ...prev, position: e.target.value } : null)}
                    className="navy-bg border-gray-600 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editingContact.email}
                    onChange={(e) => setEditingContact(prev => prev ? { ...prev, email: e.target.value } : null)}
                    className="navy-bg border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label>Mobile Number</Label>
                  <Input
                    value={editingContact.mobileNumber || ''}
                    onChange={(e) => setEditingContact(prev => prev ? { ...prev, mobileNumber: e.target.value } : null)}
                    className="navy-bg border-gray-600 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>WhatsApp Number</Label>
                  <Input
                    value={editingContact.whatsappNumber || ''}
                    onChange={(e) => setEditingContact(prev => prev ? { ...prev, whatsappNumber: e.target.value } : null)}
                    placeholder="+1234567890"
                    className="navy-bg border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label>Landline Number</Label>
                  <Input
                    value={editingContact.landlineNumber || ''}
                    onChange={(e) => setEditingContact(prev => prev ? { ...prev, landlineNumber: e.target.value } : null)}
                    placeholder="+49 30 1234567"
                    className="navy-bg border-gray-600 text-white"
                  />
                </div>
              </div>
              <div>
                <Label>Additional Mobile Numbers</Label>
                <div className="space-y-2">
                  {(editingContact.additionalMobileNumbers || []).map((num, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        value={num}
                        onChange={(e) => setEditingContact(prev => {
                          if (!prev) return null;
                          const list = [...(prev.additionalMobileNumbers || [])];
                          list[idx] = e.target.value;
                          return { ...prev, additionalMobileNumbers: list };
                        })}
                        placeholder="+1234567890"
                        className="navy-bg border-gray-600 text-white"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="shrink-0 border-gray-600"
                        onClick={() => setEditingContact(prev => {
                          if (!prev) return null;
                          const list = (prev.additionalMobileNumbers || []).filter((_, i) => i !== idx);
                          return { ...prev, additionalMobileNumbers: list };
                        })}
                        data-testid={`button-remove-mobile-${idx}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-gray-600 text-gray-300"
                    onClick={() => setEditingContact(prev => prev
                      ? { ...prev, additionalMobileNumbers: [...(prev.additionalMobileNumbers || []), ''] }
                      : null)}
                    data-testid="button-add-mobile"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add mobile number
                  </Button>
                </div>
              </div>
              {isAdmin && (
                <div className="p-3 rounded-lg border border-gray-600 bg-gray-900/50 space-y-2">
                  <Label className="flex items-center gap-1">
                    <Mail className="w-3 h-3 text-blue-400" />
                    Additional Email Addresses
                  </Label>
                  <p className="text-xs text-gray-400">
                    Emails from any of these addresses are recognized as this contact.
                  </p>
                  {(contactEmails?.aliases || []).map((a) => (
                    <div key={a.email} className="flex items-center justify-between gap-2 text-sm">
                      <span className="truncate text-gray-200">{a.email}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0 text-gray-400 hover:text-red-400"
                        onClick={() => removeAliasMutation.mutate({ contactId: editingContact.id, email: a.email })}
                        disabled={removeAliasMutation.isPending}
                        data-testid={`button-remove-alias-${a.email}`}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  {(contactEmails?.aliases || []).length === 0 && (
                    <div className="text-xs text-gray-500">No additional addresses yet.</div>
                  )}
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={newAliasEmail}
                      onChange={(e) => setNewAliasEmail(e.target.value)}
                      placeholder="other.address@example.com"
                      className="navy-bg border-gray-600 text-white"
                      data-testid="input-new-alias"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0 border-gray-600"
                      disabled={addAliasMutation.isPending || !newAliasEmail.includes('@')}
                      onClick={() => addAliasMutation.mutate({ contactId: editingContact.id, email: newAliasEmail.trim() })}
                      data-testid="button-add-alias"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select
                    value={editingContact.status}
                    onValueChange={(value) => setEditingContact(prev => prev ? { ...prev, status: value } : null)}
                  >
                    <SelectTrigger className="navy-bg border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                      <SelectItem value="On Leave">On Leave</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Preferred Communication</Label>
                  <Select
                    value={editingContact.preferredCommunication || 'email'}
                    onValueChange={(value) => setEditingContact(prev => prev ? { ...prev, preferredCommunication: value } : null)}
                  >
                    <SelectTrigger className="navy-bg border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="slack">Slack</SelectItem>
                      <SelectItem value="teams">Teams</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Happiness Baseline (%)</Label>
                <Input
                  type="number"
                  value={editingContact.happinessBaseline}
                  onChange={(e) => setEditingContact(prev => prev ? { ...prev, happinessBaseline: parseInt(e.target.value) || 50 } : null)}
                  min={0}
                  max={100}
                  placeholder="50"
                  className="navy-bg border-gray-600 text-white"
                />
              </div>
              {isAdmin && (
                <div className="grid grid-cols-2 gap-4 p-3 rounded-lg border border-gray-600 bg-gray-900/50">
                  <div>
                    <Label className="flex items-center gap-1">
                      <Shield className="w-3 h-3 text-yellow-500" />
                      Sensitivity Level
                    </Label>
                    <Select
                      value={String(editingContact.sensitivityLevel ?? 0)}
                      onValueChange={(value) => setEditingContact(prev => prev ? { ...prev, sensitivityLevel: parseInt(value) } : null)}
                    >
                      <SelectTrigger className="navy-bg border-gray-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0 - Low (Green)</SelectItem>
                        <SelectItem value="1">1 - Mild (Yellow)</SelectItem>
                        <SelectItem value="2">2 - Medium (Orange)</SelectItem>
                        <SelectItem value="3">3 - High (Red)</SelectItem>
                        <SelectItem value="4">4 - Critical (Dark Red)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Shield className="w-3 h-3 text-yellow-500" />
                      Category
                    </Label>
                    <Select
                      value={editingContact.category || 'none'}
                      onValueChange={(value) => setEditingContact(prev => prev ? { ...prev, category: value === 'none' ? null : value } : null)}
                    >
                      <SelectTrigger className="navy-bg border-gray-600 text-white">
                        <SelectValue placeholder="Select category..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Category</SelectItem>
                        {CONTACT_CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Pre-Email Template</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const standardTemplate = standardTemplates?.preTemplate || '';
                      const personalizedTemplate = standardTemplate.replace(/{firstName}/g, editingContact.firstName || 'Customer');
                      setEditingContact(prev => prev ? { ...prev, preEmailTemplate: personalizedTemplate } : null);
                    }}
                    className="text-xs h-7"
                  >
                    Use Standard Template
                  </Button>
                </div>
                <Textarea
                  value={editingContact.preEmailTemplate || ''}
                  onChange={(e) => setEditingContact(prev => prev ? { ...prev, preEmailTemplate: e.target.value } : null)}
                  className="navy-bg border-gray-600 text-white"
                  rows={3}
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Post-Email Template</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const standardTemplate = standardTemplates?.postTemplate || '';
                      setEditingContact(prev => prev ? { ...prev, postEmailTemplate: standardTemplate } : null);
                    }}
                    className="text-xs h-7"
                  >
                    Use Standard Template
                  </Button>
                </div>
                <Textarea
                  value={editingContact.postEmailTemplate || ''}
                  onChange={(e) => setEditingContact(prev => prev ? { ...prev, postEmailTemplate: e.target.value } : null)}
                  className="navy-bg border-gray-600 text-white"
                  rows={3}
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editingContact.notes || ''}
                  onChange={(e) => setEditingContact(prev => prev ? { ...prev, notes: e.target.value } : null)}
                  className="navy-bg border-gray-600 text-white"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingContact(null)}>
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  if (editingContact) {
                    updateContactMutation.mutate({
                      id: editingContact.id,
                      data: {
                        ...editingContact,
                        additionalMobileNumbers: (editingContact.additionalMobileNumbers || [])
                          .map(n => n.trim())
                          .filter(n => n.length > 0),
                      }
                    });
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Update Contact
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmation} onOpenChange={() => setDeleteConfirmation(null)}>
        <AlertDialogContent className="bg-gray-800 text-white border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              Are you sure you want to delete this {deleteConfirmation?.type === 'client' ? 'client' : 'contact person'}? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-700 text-white hover:bg-gray-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (deleteConfirmation) {
                  if (deleteConfirmation.type === 'contact') {
                    deleteContactMutation.mutate(deleteConfirmation.id);
                  }
                  setDeleteConfirmation(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ClientReportDialog
        client={reportClient}
        onClose={() => setReportClient(null)}
      />
    </div>
  );
}

type ClientProjectRow = {
  id: string;
  projectName: string;
  projectStatus: string;
  lifecycleState: string;
};

function ClientReportDialog({ client, onClose }: { client: Client | null; onClose: () => void }) {
  const { toast } = useToast();
  const open = !!client;
  const clientId = client?.id;

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [generating, setGenerating] = useState(false);

  const { data: projectRows = [], isLoading } = useQuery({
    queryKey: ["/api/clients", clientId, "projects"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/clients/${clientId}/projects`);
      if (!res.ok) throw new Error("Failed to load projects");
      return (await res.json()) as ClientProjectRow[];
    },
    enabled: open && !!clientId,
  });

  // Default-select every project the first time projects load for this client.
  // Don't overwrite the user's selection on subsequent refetches.
  const didDefaultRef = React.useRef(false);
  useEffect(() => {
    if (!open) {
      didDefaultRef.current = false;
      setSelectedIds(new Set());
      setGenerating(false);
      return;
    }
    if (open && !didDefaultRef.current && projectRows.length > 0) {
      setSelectedIds(new Set(projectRows.map((p) => p.id)));
      didDefaultRef.current = true;
    }
  }, [open, projectRows]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(projectRows.map((p) => p.id)));
  const selectNone = () => setSelectedIds(new Set());

  const handleGenerate = async () => {
    if (!clientId || selectedIds.size === 0) return;
    setGenerating(true);
    try {
      const ids = Array.from(selectedIds).join(",");
      const res = await apiRequest(
        "GET",
        `/api/clients/${clientId}/report.pdf?projectIds=${encodeURIComponent(ids)}`,
      );
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || `Request failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (client?.companyName || "client").replace(/[^a-z0-9-_]+/gi, "_").slice(0, 40);
      a.download = `${clientId}_${safe}_report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: "Report ready", description: "Your PDF download has started." });
      onClose();
    } catch (err: any) {
      toast({
        title: "Could not generate report",
        description: err?.message || "Something went wrong.",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !generating) onClose(); }}>
      <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-white">
            Generate Report — {client?.companyName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-gray-400">
            Select which projects to include. The PDF will list each project's ID, name,
            timing entries, and shots.
          </p>

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading projects…
            </div>
          ) : projectRows.length === 0 ? (
            <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-600 rounded">
              This client has no projects yet.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs text-gray-400">
                <span>{selectedIds.size} of {projectRows.length} selected</span>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-gray-300 hover:text-white"
                    onClick={selectAll}
                    disabled={generating}
                  >
                    Select all
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-gray-300 hover:text-white"
                    onClick={selectNone}
                    disabled={generating}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              <div className="max-h-80 overflow-y-auto border border-gray-700 rounded">
                {projectRows.map((p) => {
                  const checked = selectedIds.has(p.id);
                  return (
                    <label
                      key={p.id}
                      className="flex items-center gap-3 px-3 py-2 border-b border-gray-800 last:border-b-0 hover:bg-gray-800/50 cursor-pointer"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggle(p.id)}
                        disabled={generating}
                        data-testid={`checkbox-report-project-${p.id}`}
                      />
                      <Badge variant="outline" className="font-mono text-xs">
                        {p.id}
                      </Badge>
                      <span className="text-sm text-white flex-1 truncate">{p.projectName}</span>
                      <span className="text-xs text-gray-500">{p.projectStatus}</span>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={generating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={generating || selectedIds.size === 0 || projectRows.length === 0}
            data-testid="button-generate-client-report"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <FileDown className="w-4 h-4 mr-2" />
                Generate PDF
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}